// Реестр долгов (тз, раздел 5.5: «Экраны: реестр долгов … итоги и остатки»).
//
// ## Зачем отдельный экран, когда долги видны в карточке человека
//
// В карточке вопрос «сколько на этом человеке». Здесь другой и он финдиректорский: «сколько
// висит на всех, по каким типам, и что из этого удержится в этом месяце». Ответ на него
// собирался перебором карточек — двести шестьдесят семь штук.
//
// ## Почему не «просто список»
//
// Долг сам по себе не число, а обещание: у него есть остаток, график удержания и состояние.
// Строка обязана показывать все три, иначе «5 000» читается как «удержим 5 000 в этом месяце»,
// хотя удержат тысячу, и так пять месяцев.

import { useRef } from 'react';

import { Link, useLocation, useNavigate } from 'react-router';

import { RiHandCoinLine } from '@remixicon/react';

import { PrintButton } from '@/shared/reports/PrintButton';
import type { ApiError } from '@/shared/errors/api-error';
import type { DebtOut } from '@/shared/api/endpoints/losses';
import { formatKopecks, formatRubles } from '@/shared/money/format';
import { useDebtTypes } from '@/shared/dictionaries/debt-types';
import {
  ДОЛГИ_НЕ_ПЕРЕНЕСЕНЫ,
  useПереносДолгов,
  пустоУВсех,
  состояниеПереноса,
} from '@/shared/debts/перенос';
import { Подсказка } from '@/shared/hints/Подсказка';
import { useLegalEntitiesQuery } from '@/shared/org/queries';
import type { SearchState } from '@/shared/routing/search';
import { buildSearch, pushPanel } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { formatDateOnly } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { Metric, MetricRow } from '@/shared/ui/metric';
import { Сравнение } from '@/shared/reports/Сравнение';
import type { СтрокаСтруктуры } from '@/shared/reports/СтруктураСуммы';
import { СтруктураСуммы } from '@/shared/reports/СтруктураСуммы';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import * as Switch from '@/shared/ui/switch';
import { Toolbar } from '@/shared/ui/toolbar';

import { useDebtsRegistry } from '../queries';
import {
  FILTER_DEBT_STATUS,
  FILTER_DEBT_TYPE,
  FILTER_LEGAL_ENTITY,
  DEBTS_TRANSFER_ID,
  FILTER_ONLY_OPEN,
  ONLY_OPEN,
  PANEL_DEBT,
  PANEL_DEBTS_TRANSFER,
} from '../route';

/** Состояния долга словами. Приходят с сервера в самой строке; здесь только оттенок метки. */
const ТОН: Record<string, 'orange' | 'gray' | 'green' | 'red'> = {
  active: 'orange',
  repaid: 'green',
  written_off: 'gray',
  restructured: 'gray',
  suspended: 'gray',
  external_collection: 'red',
};

export function DebtsScreen({ search }: { search: SearchState }) {
  const navigate = useNavigate();
  const location = useLocation();

  const отбор = {
    legal_entity_id: search.filters[FILTER_LEGAL_ENTITY],
    debt_type: search.filters[FILTER_DEBT_TYPE],
    status: search.filters[FILTER_DEBT_STATUS],
    only_active: search.filters[FILTER_ONLY_OPEN] === ONLY_OPEN,
  };
  const долги = useDebtsRegistry(отбор);
  // Пустой реестр до переноса остатков — это «старые долги ещё не перенесены», а не «никто
  // ничего не должен». До 23.09.2026 здесь стояло второе, и заказчик прочёл его буквально.
  const перенос = состояниеПереноса(useПереносДолгов());
  const пусто = пустоУВсех(перенос, { кнопкаРядом: true });
  // Пока реестр пуст и остатки не переносились, кнопка переноса ОДНА — в пустом экране, по
  // центру, куда смотрит человек. В шапке она появляется вместе с первыми долгами: две
  // одинаковые кнопки на экране заставляют искать между ними разницу, которой нет.
  const переносВПустоте = долги.data?.items.length === 0 && перенос === 'не перенесены';
  const юрлица = useLegalEntitiesQuery();
  const типы = useDebtTypes();
  const якорьПереноса = useRef<HTMLButtonElement>(null);

  /**
   * Перенос остатков листом. Кнопка стоит на виду всегда, а не только пока реестр пуст:
   * остатки переносят и частями — сначала форму, через неделю билеты, — и после первого
   * переноса реестр уже не пуст, а дописать остальных нужно тем же путём.
   */
  function открытьПеренос() {
    void navigate({
      search: buildSearch(pushPanel(search, { view: PANEL_DEBTS_TRANSFER, id: DEBTS_TRANSFER_ID })),
    });
  }

  function сменитьОтбор(ключ: string, значение: string | null) {
    const фильтры = { ...search.filters };
    if (значение === null) {
      delete фильтры[ключ];
    } else {
      фильтры[ключ] = значение;
    }
    void navigate({ search: buildSearch({ ...search, filters: фильтры, panels: [] }) });
  }

  /**
   * Адрес карточки долга. Ссылкой, а не нажатием: строку списка копируют, открывают в соседней
   * вкладке и пересылают коллеге. Кнопка не даёт ничего из этого, а состояние экрана у нас и
   * так живёт в адресе (правило 9).
   */
  function адресДолга(долг: DebtOut): string {
    return `${location.pathname}${buildSearch(pushPanel(search, { view: PANEL_DEBT, id: долг.id }))}`;
  }

  return (
    <main className='flex min-h-0 min-w-0 flex-1 flex-col bg-bg-white-0'>
      <Toolbar
        actions={
          переносВПустоте ? undefined : (
            <Button.Root
              ref={якорьПереноса}
              type='button'
              variant='neutral'
              mode='stroke'
              size='small'
              onClick={открытьПеренос}
            >
              Перенести остатки
            </Button.Root>
          )
        }
      >
        <div className='min-w-56'>
          <SelectField
            id='debts-entity'
            label='Юрлицо'
            value={отбор.legal_entity_id ?? NO_VALUE}
            options={(юрлица.data?.items ?? []).map((строка) => ({
              value: строка.id,
              label: строка.short_name ?? строка.name,
            }))}
            noneLabel='все юрлица'
            placeholder='все юрлица'
            pending={юрлица.isPending}
            failed={юрлица.isError}
            failedError={юрлица.error as ApiError | null}
            onChange={(значение) =>
              сменитьОтбор(FILTER_LEGAL_ENTITY, значение === NO_VALUE ? null : значение)
            }
          />
        </div>
        <div className='min-w-56'>
          <SelectField
            id='debts-type'
            label='Тип долга'
            value={отбор.debt_type ?? NO_VALUE}
            options={(типы.data?.items ?? []).map((строка) => ({
              value: строка.code,
              label: строка.title,
            }))}
            noneLabel='все типы'
            placeholder='все типы'
            pending={типы.isPending}
            failed={типы.isError}
            failedError={типы.error as ApiError | null}
            onChange={(значение) =>
              сменитьОтбор(FILTER_DEBT_TYPE, значение === NO_VALUE ? null : значение)
            }
          />
        </div>
        {/* Быстрый переключатель и точный отбор по состоянию — разные вещи. Переключатель
            отвечает на «покажи только то, что ещё висит», отбор — на «покажи списанные». */}
        <label className='flex items-center gap-2 pb-2 text-paragraph-sm text-text-sub-600'>
          <Switch.Root
            checked={отбор.only_active}
            onCheckedChange={(включён) =>
              сменитьОтбор(FILTER_ONLY_OPEN, включён ? ONLY_OPEN : null)
            }
          />
          Только непогашенные
        </label>
        {/* Печать обещана тз в одном ряду с выгрузкой в Excel (раздел 5.7), и до 06.09.2026
            её не было ни у одного отчёта, кроме ведомости. Отчёты подписывают и подшивают, а
            Excel для этого надо сначала открыть на машине, где он есть. */}
        <PrintButton />
      </Toolbar>
      {/* Облачко ждёт реестр: до него неизвестно, какая из двух кнопок будет на экране, и
          привязанное к исчезнувшей оно уехало бы в угол. */}
      <Подсказка урок='перенос-остатков' якорь={якорьПереноса} когда={долги.data !== undefined} />

      <ScreenState
        data={долги.data}
        error={долги.error}
        subject='реестр долгов'
        isEmpty={(данные) => данные.items.length === 0}
        onRetry={() => void долги.refetch()}
        loading={
          <div className='flex flex-col gap-2 p-5'>
            <Skeleton className='h-20 w-full' />
            <Skeleton className='h-20 w-full' />
          </div>
        }
        empty={
          <div className='p-5'>
            <EmptyState
              icon={RiHandCoinLine}
              title={
                отбор.debt_type !== undefined ||
                отбор.legal_entity_id !== undefined ||
                отбор.only_active
                  ? 'По этому отбору долгов нет'
                  : пусто.title
              }
              description={
                отбор.debt_type !== undefined ||
                отбор.legal_entity_id !== undefined ||
                отбор.only_active
                  ? перенос === 'не перенесены'
                    ? `Снимите отбор, чтобы увидеть остальные. ${ДОЛГИ_НЕ_ПЕРЕНЕСЕНЫ.replace('сюда ещё', 'ещё')}`
                    : 'Снимите отбор, чтобы увидеть остальные долги.'
                  : пусто.description
              }
              action={
                перенос === 'не перенесены' ? (
                  <Button.Root
                    ref={якорьПереноса}
                    type='button'
                    variant='primary'
                    mode='filled'
                    size='small'
                    onClick={открытьПеренос}
                  >
                    Перенести остатки
                  </Button.Root>
                ) : undefined
              }
            />
          </div>
        }
      >
        {(данные) => {
          const остаток = данные.items.reduce((сумма, долг) => сумма + долг.outstanding, 0);
          // План месяца считает СЕРВЕР (`month_plan`): единица значения графика зависит от
          // способа, и до 19.10.2026 эта плитка складывала его как деньги — «6 месяцев»
          // становились шестью копейками, «25 %» — двадцатью пятью. План один на месяц человека,
          // сколько бы ведомостей у него ни было.
          const вМесяц = данные.items.reduce((сумма, долг) => сумма + (долг.month_plan ?? 0), 0);
          // Процент от выплаты заранее не сказать: он зависит от того, сколько человек
          // заработает. Такие долги сервер отдаёт пустым планом, и они называются отдельно, а не
          // прячутся нулём в сумме. Плана нет вовсе (поле не пришло) — сервер старее экрана.
          const процентом = данные.items.filter((долг) => долг.month_plan === null);
          const люди = new Set(данные.items.map((долг) => долг.employee_id));
          // Целиком удерживается только долг с графиком «весь остаток ближайшим расчётом». До
          // 19.10.2026 сюда попадал всякий долг с пустым значением графика — и форма, которая
          // по умолчанию удерживается тысячей в месяц, и долг «вручную», который расчёт не
          // трогает вовсе. Это та часть плана, которая удивляет человека в день выплаты.
          const целиком = данные.items.filter(
            (долг) =>
              долг.schedule_type === 'full_at_once' &&
              долг.status === 'active' &&
              долг.outstanding > 0,
          );
          const целикомСумма = целиком.reduce((сумма, долг) => сумма + долг.outstanding, 0);

          return (
            <div className='flex min-h-0 flex-1 flex-col'>
              {/* Ряд величин стоит в тех же полях, что и такие же ряды в ведомости и в
                  официальном контуре. Без них он шёл от края до края экрана, и последняя
                  величина — «Людей с долгом» с пояснением в строку — упиралась в правый край
                  без единой точки отступа: на кадре 1680 читается как обрезанная. Отступ
                  здесь, а не в примитиве: примитив — копия дизайн-системы, и поля вокруг
                  ряда решает тот, кто его ставит. */}
              <MetricRow columns={4} className='shrink-0 px-5 py-3'>
                <Metric
                  tone='важный'
                  label='Остаток по всем долгам'
                  value={formatRubles(остаток)}
                  note={`${countLabel(данные.items.length, ['долг', 'долга', 'долгов'])} у ${countLabel(люди.size, ['человека', 'человек', 'человек'])}`}
                />
                <Metric
                  label='Удержится в месяц'
                  value={formatRubles(вМесяц)}
                  note={
                    процентом.length === 0
                      ? 'по графикам, один раз на месяц человека, если начисленного хватит'
                      : `по графикам, если начисленного хватит; ещё ${countLabel(процентом.length, ['долг', 'долга', 'долгов'])} процентом от выплаты: сумму заранее не сказать`
                  }
                />
                <Metric
                  label='Удержится целиком'
                  value={formatRubles(целикомСумма)}
                  note={
                    целиком.length === 0
                      ? 'долгов «весь остаток разом» нет'
                      : `${countLabel(целиком.length, ['долг', 'долга', 'долгов'])} «весь остаток разом»: удержим одной суммой в ближайшем расчёте`
                  }
                />
                <Metric
                  label='Людей с долгом'
                  value={String(люди.size)}
                  note='каждый может числиться по нескольким долгам сразу'
                />
              </MetricRow>

              {/* Два разреза одного и того же реестра, и оба отвечают на вопросы, которых
                  плоский список не закрывал.

                  «Из чего сложился остаток» — полоса по типам долга: сто строк списка не
                  говорят, что половину долга составляет форма, а половину билеты. Форма та
                  же, что у долгов в сводке за месяц: вопрос один, вид один.

                  «У кого больше всех» — столбцы: список отсортирован не по сумме, а по людям
                  и датам, и найти в нём десять самых крупных должников глазами нельзя.

                  Оба разреза считаются по УЖЕ ПРИЕХАВШИМ строкам, и только когда приехали
                  ВСЕ. Посчитанное по куску и названное итогом хуже пустого места: пустое
                  место не врёт. Тот же приём и та же оговорка, что у состава реестра людей. */}
              {/* Разрезы и список лежат в ОДНОЙ прокрутке, а ряд величин над ними закреплён.

                  Так не было до 09.09.2026, и первый же кадр это показал: ряд величин стоял
                  гибким элементом рядом с прокручиваемым списком, разрезы добавили высоты, и
                  ряд сжался — от четырёх сумм остались одни подписи, а сами числа срезало по
                  верхнему краю. Это ровно то, от чего в блоке стоит `shrink-0`.
                  Закрепить разрезы тем же способом было нельзя: на ноутбучном экране они
                  съели бы список целиком, а список здесь и есть работа.

                  Ряд величин отвечает «сколько всего» и обязан быть виден всегда. Разрезы
                  отвечают «из чего» и «у кого» — их читают один раз и уходят вниз к людям. */}
              <div className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
                {данные.items.length === данные.total && данные.items.length > 1 && (
                  <div className='flex shrink-0 flex-col gap-5 border-t border-stroke-soft-200 px-5 py-4'>
                    <СтруктураСуммы
                      label='Из чего сложился остаток'
                      total={остаток}
                      // Итог крупным числом НЕ повторяется: он уже стоит первой величиной
                      // в ряду над блоком. Два одинаковых числа подряд человек читает как две
                      // разные величины и ищет между ними разницу.
                      showTotal={false}
                      nameHeader='Тип долга'
                      noteHeader='Людей'
                      rows={поТипам(данные.items)}
                    />
                    {топДолжников(данные.items).length > 1 && (
                      <div className='flex flex-col gap-2'>
                        <span className='text-label-sm text-text-strong-950'>
                          У кого долг больше всех
                        </span>
                        <Сравнение
                          rows={топДолжников(данные.items)}
                          formatValue={formatRubles}
                          yAxisWidth={140}
                        />
                      </div>
                    )}
                  </div>
                )}

                <ul
                  data-печать='документ'
                  className='divide-y divide-stroke-soft-200 border-t border-stroke-soft-200'
                >
                  {данные.items.map((долг) => (
                    <li key={долг.id}>
                      <Link
                        to={адресДолга(долг)}
                        className='flex w-full items-center justify-between gap-4 px-5 py-3 text-left transition duration-200 ease-out hover:bg-bg-weak-50'
                      >
                        <div className='flex min-w-0 flex-col gap-0.5'>
                          <span className='truncate text-label-sm text-text-strong-950'>
                            {долг.employee_name}
                          </span>
                          <span className='truncate text-paragraph-xs text-text-sub-600'>
                            {долг.debt_type_title}
                            {долг.description ? ` · ${долг.description}` : ''} · заведён{' '}
                            {formatDateOnly(долг.created_at)}
                            {/* Фраза графика — сервера: «1 000 ₽ в месяц», «равными долями за 3
                                месяца». Собирать её здесь из значения графика нельзя — его
                                единица зависит от способа, и экран напечатал бы «6 ₽». */}
                            {долг.schedule_note ? ` · ${долг.schedule_note}` : ''}
                          </span>
                        </div>
                        <div className='flex shrink-0 items-center gap-3'>
                          <Badge.Root
                            variant='lighter'
                            color={ТОН[долг.status] ?? 'gray'}
                            size='medium'
                          >
                            {долг.status_title ?? долг.status}
                          </Badge.Root>
                          <div className='flex flex-col items-end'>
                            {/* Знака рубля нет: столбец целиком денежный, единицу назвал
                              заголовок «Остаток по всем долгам» наверху. */}
                            <span className='tabular-nums text-label-sm text-text-strong-950'>
                              {formatKopecks(долг.outstanding)}
                            </span>
                            <span className='tabular-nums text-paragraph-xs text-text-sub-600'>
                              из {formatKopecks(долг.initial_amount)}
                            </span>
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        }}
      </ScreenState>
    </main>
  );
}

/**
 * Остаток по типам долга, от большего к меньшему.
 *
 * Долей в процентах здесь нет и быть не может: сервер их по долгам не считает, а посчитать
 * на экране значит завести второй ответ на один вопрос. Долю показывает ширина сегмента —
 * это сама величина, а не пересчитанное число.
 */
function поТипам(долги: DebtOut[]): СтрокаСтруктуры[] {
  const по: Map<string, { title: string; amount: number; люди: Set<string> }> = new Map();
  for (const долг of долги) {
    if (долг.outstanding <= 0) continue;
    const строка = по.get(долг.debt_type) ?? {
      title: долг.debt_type_title,
      amount: 0,
      люди: new Set<string>(),
    };
    строка.amount += долг.outstanding;
    строка.люди.add(долг.employee_id);
    по.set(долг.debt_type, строка);
  }
  return [...по.entries()]
    .map(([код, строка]) => ({
      id: код,
      title: строка.title,
      amount: строка.amount,
      note: String(строка.люди.size),
    }))
    .sort((а, б) => б.amount - а.amount);
}

/**
 * Восемь человек с самым большим остатком.
 *
 * Восемь, а не десять и не двадцать: столбцов больше восьми на экране перестают сравниваться
 * — глаз держит их ряд целиком, пока строк меньше десятка, а дальше начинает читать по одной,
 * то есть возвращается к списку.
 *
 * Фамилия сокращается до «Багровский С. С.»: полная строка в подписи столбца занимает треть
 * ширины, и на её место не остаётся самого столбца.
 */
function топДолжников(долги: DebtOut[]) {
  const по = new Map<string, { name: string; value: number }>();
  for (const долг of долги) {
    if (долг.outstanding <= 0) continue;
    const строка = по.get(долг.employee_id) ?? { name: сократитьИмя(долг.employee_name), value: 0 };
    строка.value += долг.outstanding;
    по.set(долг.employee_id, строка);
  }
  return [...по.entries()]
    .map(([id, строка]) => ({ id, name: строка.name, value: строка.value }))
    .sort((а, б) => б.value - а.value)
    .slice(0, 8);
}

function сократитьИмя(полное: string): string {
  const части = полное.trim().split(/\s+/);
  if (части.length <= 1) return полное;
  const инициалы = части
    .slice(1)
    .map((часть) => `${часть[0] ?? ''}.`)
    .join(' ');
  return `${части[0]} ${инициалы}`;
}
